import React from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    type SelectChangeEvent,
    Chip,
    Button,
    Checkbox,
    Radio,
    Switch,
    Box,
    FormControlLabel,
    TextField,
    MenuItem,
    ListItemText,
} from '@mui/material';
import { Controller, type ControllerRenderProps, type FieldValues } from 'react-hook-form';

import { type Option, renderOptions } from '@/src/utils';
import ErrorMessage from './ErrorMessage';
import type { Colors } from './styling';
import { OutlinedInput, Typography } from '@mui/material';

// extend Option
interface ExtendedOption extends Option {
    archived?: boolean;
}

interface ControlledSingleSelectProps {
    name: string;
    label: string;
    onChange?: (
        event: SelectChangeEvent<unknown>,
        field: ControllerRenderProps<FieldValues, string>
    ) => void;
    control: any;
    options: Array<Option>;
    error?: any;
    sx?: React.CSSProperties;
    disabled?: boolean;
}

export const ControlledSingleSelect: React.FC<ControlledSingleSelectProps> = ({
    name,
    label,
    onChange,
    control,
    options,
    error,
    sx = { width: '100%', mb: 2 },
    disabled,
}) => {
    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <FormControl size="small" sx={sx} disabled={disabled}>
                    <InputLabel id={name}>{label}</InputLabel>
                    <Select
                        {...field}
                        labelId={name}
                        label={label}
                        onChange={(event) =>
                            onChange ? onChange(event, field) : field.onChange(event.target.value)
                        }
                    >
                        {renderOptions(options)}
                    </Select>
                    <ErrorMessage error={error} />
                </FormControl>
            )}
        />
    );
};

interface ControlledChipMultiSelectProps {
    name: string;
    label: string;
    onChange?: (
        event: SelectChangeEvent<unknown>,
        field: ControllerRenderProps<FieldValues, string>
    ) => void;
    control: any;
    options: Array<ExtendedOption>;
    maxHeight?: number;
    renderExtras?: (field: any) => React.ReactNode;
    sx?: React.CSSProperties;
    error?: any;
    disabled?: boolean;
    hideClear?: boolean;
}

export const ControlledChipMultiSelect: React.FC<ControlledChipMultiSelectProps> = ({
    name,
    label,
    onChange,
    control,
    options,
    maxHeight = 400,
    renderExtras,
    sx = { width: '100%' },
    error,
    disabled,
    hideClear,
}) => {
    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FormControl size="small" sx={sx} disabled={disabled} error={!!error}>
                        <InputLabel>{label}</InputLabel>
                        <Select
                            {...field}
                            multiple
                            labelId={name}
                            label={label}
                            value={field.value || []}
                            onChange={(event) =>
                                onChange
                                    ? onChange(event, field)
                                    : field.onChange(event.target.value)
                            }
                            renderValue={(selected) =>
                                Array.isArray(selected) ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((id) => {
                                            const opt = options.find((o) => o.id === id);
                                            const label = opt ? opt.name : id;
                                            return (
                                                <Chip
                                                    key={id}
                                                    label={
                                                        <span
                                                            style={{
                                                                textDecoration: opt?.archived
                                                                    ? 'line-through'
                                                                    : 'none',
                                                            }}
                                                        >
                                                            {label}
                                                        </span>
                                                    }
                                                    size="small"
                                                    sx={{
                                                        maxWidth: 120,
                                                        fontSize: '0.8rem',
                                                        border: opt?.archived
                                                            ? '1px solid red'
                                                            : undefined,
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    ''
                                )
                            }
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight,
                                    },
                                },
                            }}
                        >
                            {renderOptions(options)}
                        </Select>
                        <ErrorMessage error={error} />
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1, ml: 1, alignItems: 'center' }}>
                        {renderExtras && renderExtras(field)}

                        {!hideClear && (
                            <Button
                                size="small"
                                variant="outlined"
                                sx={{ minWidth: 0, px: 1 }}
                                onClick={() => field.onChange([])}
                            >
                                Clear
                            </Button>
                        )}
                    </Box>
                </Box>
            )}
        />
    );
};

interface ControlledChipDisplayProps {
    name: string;
    label: string;
    control: any;
    options: Array<ExtendedOption>;
    sx?: React.CSSProperties;
    error?: any;
    readOnly?: boolean;
}

export const ControlledChipDisplay: React.FC<ControlledChipDisplayProps> = ({
    name,
    label,
    control,
    options,
    sx = { width: '100%', mb: 2 },
    error,
    readOnly,
}) => {
    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                const selected: number[] = field.value || [];

                return (
                    <FormControl size="small" sx={sx} error={!!error} disabled={readOnly}>
                        <InputLabel>{label}</InputLabel>
                        <Select
                            multiple
                            value={selected}
                            input={<OutlinedInput notched={selected.length > 0} label={label} />}
                            onOpen={() => false} // Prevent dropdown
                            sx={{ pointerEvents: 'none' }} // Disable clicks
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((id) => {
                                        const opt = options.find((o) => o.id === id);
                                        const label = opt ? opt.name : id;
                                        return (
                                            <Chip
                                                key={id}
                                                label={
                                                    <span
                                                        style={{
                                                            textDecoration: opt?.archived
                                                                ? 'line-through'
                                                                : 'none',
                                                        }}
                                                    >
                                                        {label}
                                                    </span>
                                                }
                                                size="small"
                                                sx={{
                                                    maxWidth: 120,
                                                    fontSize: '0.8rem',
                                                    border: opt?.archived
                                                        ? '1px solid red'
                                                        : undefined,
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                            MenuProps={{ PaperProps: { style: { display: 'none' } } }}
                            IconComponent={readOnly ? () => null : undefined}
                        >
                            {options.map((o) => (
                                <MenuItem key={o.id} value={o.id}>
                                    {o.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            }}
        />
    );
};

interface ControlledTextFieldProps {
    name: string;
    label: string;
    control: any;
    variant?: 'outlined' | 'filled' | 'standard';
    error?: any;
    type?: 'input' | 'textarea' | 'number' | 'datetime-local' | 'password';
    autocomplete?: 'new-password' | 'off' | 'on' | string;
    placeholder?: string;
    inputProps?: any;
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        field: ControllerRenderProps<FieldValues, string>
    ) => void;
    sx?: React.CSSProperties;
    disabled?: boolean;
    maxLength?: number;
}

export const ControlledTextField: React.FC<ControlledTextFieldProps> = ({
    name,
    label,
    control,
    variant = 'outlined',
    type = 'input',
    autocomplete = 'off',
    placeholder,
    inputProps = {},
    onChange,
    sx = { width: '100%', mb: 2 },
    error,
    disabled,
    maxLength,
}) => {
    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => {
                const valueLength = field.value?.length || 0;

                return (
                    <Box display="flex" flexDirection="column">
                        <TextField
                            {...field}
                            label={label}
                            variant={variant}
                            fullWidth
                            type={type}
                            autoComplete={autocomplete}
                            multiline={type === 'textarea'}
                            placeholder={placeholder}
                            margin="normal"
                            onChange={(event) =>
                                onChange
                                    ? onChange(event, field)
                                    : field.onChange(event.target.value)
                            }
                            slotProps={{ htmlInput: inputProps }}
                            error={!!error}
                            helperText={error?.message}
                            sx={sx}
                            disabled={disabled}
                        />
                        {maxLength && (
                            <Typography
                                variant="caption"
                                sx={{
                                    textAlign: 'right',
                                    color: valueLength > maxLength ? 'red' : 'gray',
                                    mt: '-12px',
                                    mb: '-6px',
                                }}
                            >
                                {valueLength} / {maxLength}
                            </Typography>
                        )}
                    </Box>
                );
            }}
        />
    );
};

interface ControlledCheckboxProps {
    name: string;
    label?: string;
    checked?: boolean;
    control: any;
    color?: Colors;
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        field: ControllerRenderProps<FieldValues, string>
    ) => void;
    sx?: React.CSSProperties;
    disabled?: boolean;
}

export const ControlledCheckbox: React.FC<ControlledCheckboxProps> = ({
    name,
    label,
    checked,
    control,
    color = 'primary',
    onChange,
    sx = {},
    disabled,
}) => {
    return (
        <Box sx={{ mb: 1 }}>
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={checked === undefined ? !!field.value : checked}
                                onChange={(event) =>
                                    onChange
                                        ? onChange(event, field)
                                        : field.onChange(event.target.checked)
                                }
                                color={color}
                                sx={sx}
                                disabled={disabled}
                            />
                        }
                        label={label}
                    />
                )}
            />
        </Box>
    );
};

interface ControlledRadioProps {
    name: string;
    label?: string;
    control: any;
    color?: Colors;
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        field: ControllerRenderProps<FieldValues, string>
    ) => void;
    checkedValue?: any;
    sx?: React.CSSProperties;
    disabled?: boolean;
}

export const ControlledRadio: React.FC<ControlledRadioProps> = ({
    name,
    label,
    control,
    color = 'primary',
    onChange,
    checkedValue = true,
    sx = {},
    disabled,
}) => {
    return (
        <Box sx={{ mb: 1 }}>
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <FormControlLabel
                        control={
                            <Radio
                                checked={field.value === checkedValue}
                                onChange={(event) =>
                                    onChange ? onChange(event, field) : field.onChange(checkedValue)
                                }
                                color={color}
                                sx={sx}
                                disabled={disabled}
                            />
                        }
                        label={label}
                    />
                )}
            />
        </Box>
    );
};

interface ControlledSwitchProps {
    name: string;
    label?: string;
    control: any;
    color?: Colors;
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement>,
        field: ControllerRenderProps<FieldValues, string>
    ) => void;
    labelPlacement?: 'bottom' | 'end' | 'start' | 'top';
    sx?: React.CSSProperties;
    disabled?: boolean;
}

export const ControlledSwitch: React.FC<ControlledSwitchProps> = ({
    name,
    label,
    control,
    color = 'primary',
    onChange,
    labelPlacement,
    sx = {},
    disabled,
}) => {
    return (
        <Box sx={{ mb: 1 }}>
            <Controller
                name={name}
                control={control}
                render={({ field }) => (
                    <FormControlLabel
                        control={
                            <Switch
                                name={name}
                                color={color}
                                onChange={(event) =>
                                    onChange
                                        ? onChange(event, field)
                                        : field.onChange(event.target.checked)
                                }
                                checked={!!field.value}
                                sx={sx}
                                disabled={disabled}
                            />
                        }
                        label={label}
                        labelPlacement={labelPlacement}
                    />
                )}
            />
        </Box>
    );
};

interface ControlledMultipleSelectProps {
    name: string;
    label: string;
    control: any;
    value: number[]; // multiple selected values
    onChange?: (event: any, field: ControllerRenderProps<FieldValues, string>) => void;
    options: Option[];
    sx?: React.CSSProperties;
    error?: any;
    disabled?: boolean;
}

export const ControlledMultipleSelect: React.FC<ControlledMultipleSelectProps> = ({
    name,
    label,
    control,
    value,
    onChange,
    options,
    sx = { width: '100%', mb: 2 },
    error,
    disabled,
}) => {
    return (
        <Controller
            name={name}
            control={control}
            render={({ field }) => (
                <FormControl size="small" sx={sx} disabled={disabled}>
                    <InputLabel id={`${name}-label`}>{label}</InputLabel>
                    <Select
                        labelId={`${name}-label`}
                        multiple
                        value={value}
                        onChange={(event) =>
                            onChange ? onChange(event, field) : field.onChange(event.target.value)
                        }
                        label={label}
                        renderValue={(selected) => {
                            if (!selected || selected.length === 0) return <em>{label}</em>;
                            return options
                                .filter((opt) => selected.map(Number).includes(Number(opt.id)))
                                .map((opt) => opt.name)
                                .join(', ');
                        }}
                    >
                        {options.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                <Checkbox checked={value.includes(Number(option.id))} />
                                <ListItemText primary={option.name} />
                            </MenuItem>
                        ))}
                    </Select>
                    <ErrorMessage error={error} />
                </FormControl>
            )}
        />
    );
};
